/**
 * New Year story backgrounds — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearStoryBackgroundsPack: ToilePack = {
    slug: 'new-year-story-backgrounds',
    category: 'season',
    name: 'New Year story backgrounds',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'confetti', label: 'Confetti frame', subject: 'A tall background with confetti and streamers framing the edges and a calm dark centre left empty for a caption'},
        {key: 'skyline', label: 'Skyline', subject: 'A tall background of a city skyline with fireworks at the bottom, a calm sky above left empty for a caption'},
        {key: 'deco', label: 'Deco panel', subject: 'A tall background with an art deco frame and a large blank panel in the middle left empty for a caption'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['with a countdown clock added', 'in daylight instead of night', 'as a square crop'],
    directionHint: 'try: with a countdown clock added',
    seo: {
        title: 'New Year Instagram Story Background: Confetti, Skyline, Deco',
        metaDescription: 'Nine new year story backgrounds — confetti frame, skyline, deco frame — tall with room for your own text, in three styles.',
        h1: 'New year story background: confetti, skyline, deco frame',
        tagline: 'Nine backgrounds, three midnight subjects, three styles each.',
        intro: 'Tall, phone-shaped backgrounds for the New Year story: a confetti frame, a calm skyline, a deco frame, each left open in the middle for your own text or sticker. Ask for a countdown clock backdrop, a party-invite frame, or your own idea, and the same three styles carry it.',
        useCases: [
            'an Instagram or Snapchat story background',
            'a countdown-to-midnight story series',
            'a party invite shared as a story',
            "a phone lock screen for New Year's Eve",
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine tall story backgrounds — a confetti frame, a skyline, a deco frame — each with a clear middle for text, in three styles.'},
            {q: 'What size are these, and will they fit a phone screen?', a: 'Each renders in a tall, phone-shaped ratio built for Instagram and Snapchat stories, and works as a lock screen too.'},
            {q: 'Is there room for me to add my own text or stickers?', a: "Yes — the middle of the frame is left open so your app's text and sticker tools sit on top cleanly."},
            {q: "Can I use these for my business's social account?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
            {q: 'Can I get a countdown clock instead of a skyline?', a: 'Yes — the direction field takes any new year subject, including a countdown clock or a party invite layout.'},
        ],
    },
}
